//Calendar needs to be migrated
<template>
  <div class="charter-destination__deal-finder-calendar mt-1">
    <div
      v-if="error"
      class="charter-destination__deal-finder-calendar-overlay"
    >
      <LazyError max-width>
        <slot name="error" />
      </LazyError>
    </div>
    <table
      v-if="loading || !calendarLoaded"
      class="table w-full"
    >
      <tr>
        <th width="20" />
        <th
          v-for="i in 7"
          :key="`th-${i}`"
        >
          <b-skeleton />
        </th>
      </tr>
      <tr
        v-for="i in 4"
        :key="`tr-${i}`"
      >
        <th />
        <td
          v-for="x in 7"
          :key="`td-${i}-${x}`"
        >
          <b-skeleton height="5.5rem" />
        </td>
      </tr>
    </table>
    <Calendar
      ref="calendar"
      class="!w-full"
      v-bind="calendarConfig"
      :show-weeknumbers="true"
      borderless
      :class="{ '--day-clickable': !$isSol, '!hidden': loading || !calendarLoaded }"
      prevent-loading-indicator
      @loaded="calendarLoaded = true"
      @dayclick="dayClicked"
      @did-move="calendarSwipeMoved"
    >
      <template #day-content="props">
        <span
          class="dealfinder-date__day z-[10]"
          :class="{ 'opacity-25': props.day.isDisabled, 'text-white font-bold': isDateHighlighted(props.day) }"
        >{{ props.day.day }}</span>
        <button
          v-for="(day, index) in dealDate(props.day)"
          :key="index"
          class="font-bold text-center dealfinder-date"
          :class="day.classes"
          @click="$emit('select', day)"
        >
          <div>
            <span
              class="dealfinder-date__date text-nowrap md:!hidden"
              :class="{ '!block': hasLocaleOpenPurchase && day.recommended_flight }"
            >{{ $t('daysTextShort', day.days) }}</span>
            <span
              class="dealfinder-date__date text-nowrap hide-mobile"
              :class="{ '!block': hasLocaleOpenPurchase && day.recommended_flight }"
            >{{ $t('daysText', day.days) }}</span>
            <span
              class="dealfinder-date__price text-nowrap"
              :class="[isDiscounted(day) ? ' text-red' : '', { '!inline': hasLocaleOpenPurchase && day.recommended_flight }]"
            >{{ $n(day.price) }}</span>
          </div>
        </button>
      </template>
    </Calendar>
    <slot name="footer" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { DATEPICKER_DATE_FORMAT } from '@packages/date/formats'

import events from '@layers/web/constants/events.js'

export default defineNuxtComponent({
  setup () {
    const { localeDate } = useDate()

    return {
      localeDate,
    }
  },

  props: {
    error: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'select',
    'loaded',
    events.dealfinderUpdateCalendarDate,
  ],

  data () {
    return {
      bestPriceDate: null,
      calendarLoaded: false,
    }
  },

  computed: {
    ...mapState(useCharterStore, {
      prefillSelectedStartDate: 'selectedStartDate',
    }),

    ...mapState(useDealfinderStore, {
      selectedDuration: 'selectedDuration',
      dates: 'dealDates',
      startDate: 'startDate',
    }),

    ...mapState(useDealfinderStore, {
      months: 'cheapestDateList',
      currentActiveIndex: 'currentActiveIndex',
    }),

    ...mapState(useLocaleStore, ['hasLocaleOpenPurchase']),

    calendarConfig () {
      return {
        minDate: this.$dayjs().toDate(),
        disabledDates: [{
          every: 'month',
          repeat: {
            on: (day) => !this.calendarDays.includes(this.$dayjs(day.date).format(DATEPICKER_DATE_FORMAT)),
          },
        }],
      }
    },

    calendarDays () {
      return (this.dates || []).map(d => this.$dayjs(d.date).format(DATEPICKER_DATE_FORMAT)) || ['1/1/1970']
    },
  },

  watch: {
    selectedDuration () {
      this.resetCalendar()
    },
  },

  created () {
    if (process.browser) {
      this.initializeWhenReadyAbortController = new AbortController()
    }
  },

  mounted () {
    this.selectedStartDate = { ...this.prefillSelectedStartDate }
    this.$event.$on(events.dealfinderUpdateCalendarDate, this.onUpdate)
    this.resetCalendar()
  },

  beforeUnmount () {
    this.initializeWhenReadyAbortController?.abort()
    this.$event.$off(events.dealfinderUpdateCalendarDate, this.onUpdate)
  },

  methods: {

    ...mapActions(useDealfinderStore, {
      activeDateInSlide: 'SET_DEALFINDER_CURRENT_SLIDE_DATE',
    }),

    dealDate (date) {
      date = this.localeDate(date.date)
      const queryDate = this.$route.query?.date

      return (this.dates || [])
        .filter(d => d.date === date)
        .map(day => ({
          ...day,
          classes: {
            highlighted: queryDate === day.date,
            blink: queryDate === day.date,
            '!bg-blue-special': this.hasLocaleOpenPurchase && day.recommended_flight,
            'best-price': (!this.hasLocaleOpenPurchase || !day.open_purchase) && this.isDiscounted(day),
            'adjacent-dates-carousel-slide-special-date__best-price': this.isBestPriceDay(day),
          },
        }))
    },

    isDateHighlighted (day) {
      return this.dealDate(day).some(entry => entry.classes.highlighted)
    },

    isBestPriceDay (day) {
      if (!this.$isSol) {
        return false
      }

      const formattedDay = this.$dayjs(day.date).format('M/D/YYYY')
      const month = this.$dayjs(formattedDay).month()
      const year = this.$dayjs(formattedDay).year()
      // Check if there are any dates in the same month with the key 'recommended_flight'
      const hasRecommendedFlight = this.dates.some(date => {
        const dateMonth = this.$dayjs(date.date).month()
        const dateYear = this.$dayjs(date.date).year()
        return dateMonth === month && dateYear === year && date.recommended_flight === 1
      })

      if (hasRecommendedFlight) {
        return false
      }

      return this.bestPriceDate === formattedDay
    },

    isDiscounted (date) {
      return dealfinderUtils.dateIsDiscounted(date)
    },

    dayClicked (day) {
      if (this.$isSol) {
        return
      }
      const deals = this.dealDate(day)
      if ((deals || []).length === 1) {
        this.$emit('select', deals[0])
      }
    },

    calendarSwipeMoved (date) {
      if (!date) {
        return
      }
      this.activeDateInSlide(this.$dayjs(date.monthComps.firstDayOfMonth).format(DATEPICKER_DATE_FORMAT))
    },

    onUpdate (date) {
      this.updateDate(date)
    },

    async updateDate (date) {
      await promiseUntilCondition(() => (this.$refs?.calendar?.vcalendar), {
        abortSignal: this.initializeWhenReadyAbortController?.signal,
      })
        .then(() => {
          this.bestPriceDate = date,
          this.$refs.calendar.vcalendar.move(this.$dayjs(date, DATEPICKER_DATE_FORMAT).toDate())
        })
        .catch((e) => {
          if (e.name !== 'AbortError') {
            this.$sentryCaptureException('updateCalendar/can\'t find calendar reference', {
              level: 'error',
              tags: { type: 'dealfinder' },
            })
          }
        })
        .finally(() => (this.$event.$emit(events.dealfinderLoading, false)))
    },

    resetCalendar () {
      if ((this.months[0] && this.currentActiveIndex() === 0) || (this.currentActiveIndex() + 1 > this.months.length)) {
        if (this.months[0]) {
          this.updateDate(this.$dayjs.unix(this.months[0].ts).format(DATEPICKER_DATE_FORMAT))
        }
      } else {
        this.updateDate(this.$dayjs.unix(this.months[this.currentActiveIndex()].ts).format(DATEPICKER_DATE_FORMAT))
      }
    },
  },
})
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/dealfinder/_calendar";
</style>
